import { useEffect, useMemo, useRef, useState } from 'react';

import { LoaderFunctionArgs, json } from '@remix-run/node';
import { useNavigation, useSearchParams } from '@remix-run/react';
import CustomPagination from '~/components/custom-pagination';
import { Home } from '~/components/images/Home';
import Layout from '~/components/layout';
import { ProductCard } from '~/components/product-card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~/components/ui/breadcrumb';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { useRevalidateOnLotClose } from '~/hooks/use-revalidate-on-lot-close';
import { capitalizeFirstLetter } from '~/lib/utils';
import { checkRateLimit, getClientIp } from '~/lib/rate-limit.server';
import { getAuctionHouseListWithoutPagination } from '~/services/api/auction-houses';
import { validateUser } from '~/services/api/auth';
import { getCategoryList } from '~/services/api/categories';
import { getProductList } from '~/services/api/products';
import { AuctionType } from '~/types';
import { Filter, LoaderCircle, Search, X } from 'lucide-react';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

import ExploreCategories from '../_index/categories';
import FilterSideBar from '../../components/filter-side-bar';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const ip = getClientIp(request);
  if (!checkRateLimit(ip)) {
    throw json({ error: 'Too many requests. Please slow down.' }, { status: 429 });
  }

  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;
  const selectedCategory = url.searchParams.get('category');
  const auctionDate = url.searchParams.get('date') || undefined;
  const auctionType = url.searchParams.get('type') || undefined;
  const selectedCategories = url.searchParams.getAll('category') || [];
  const selectedAuctionHouses = url.searchParams.getAll('seller') || [];
  const search = url.searchParams.get('search') || undefined;

  const [{ user }, products, categories, auctionHouses] = await Promise.all([
    validateUser(request),
    getProductList({
      limit,
      page,
      sortByLotNumber: false,
      approvedOnly: true,
      ...(auctionDate && { auctionDate: new Date(auctionDate?.replace('+', '/')) }),
      ...(auctionType && { auctionType }),
      categories: selectedCategories,
      auctionHouses: selectedAuctionHouses,
      ...(search && { search }),
    }),
    getCategoryList(),
    getAuctionHouseListWithoutPagination(),
  ]);

  return typedjson({
    categories,
    products,
    user,
    auctionHouses,
    selectedCategory,
    search: search ?? '',
  });
};

export default function Products() {
  const data = useTypedLoaderData<typeof loader>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { categories, products, auctionHouses } = data;
  const [selectedCategory, setSelectedCategory] = useState(data.selectedCategory || '');
  const [openFilter, setOpenFilter] = useState(false);
  const [searchInput, setSearchInput] = useState(data.search ?? '');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productsData = useMemo(() => products.list, [products.list]);
  const navigation = useNavigation();

  // This page has no Pusher subscription, so without this the cards flip at
  // time zero using the bid counts captured when the page was first loaded.
  useRevalidateOnLotClose(productsData.map((product) => product.cutOffTime));

  const handleCategory = (categoryName: string) => {
    setSelectedCategory(categoryName);
  };

  const currentPage = data.products.page;
  const totalPages = Math.ceil(data.products.totalCount / data.products.limit);

  useEffect(() => {
    const searchParamsCategory = searchParams.get('category');
    if (searchParamsCategory) {
      setSelectedCategory(searchParamsCategory);
    }
  }, [searchParams]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set('search', value);
      } else {
        next.delete('search');
      }
      next.delete('page');
      setSearchParams(next, { preventScrollReset: true });
    }, 350);
  };

  const handleSearchClear = () => {
    setSearchInput('');
    const next = new URLSearchParams(searchParams);
    next.delete('search');
    next.delete('page');
    setSearchParams(next, { preventScrollReset: true });
  };

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/" className="flex flex-row items-center gap-1 text-sm font-normal">
              <Home className="h-5 w-5" />
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          {selectedCategory ? (
            <>
              <BreadcrumbItem>
                <BreadcrumbLink href="/products" className="text-sm font-normal">
                  Products
                </BreadcrumbLink>
              </BreadcrumbItem>
              {selectedCategory && (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage className="text-sm font-normal">
                      {capitalizeFirstLetter(selectedCategory)}
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              )}
            </>
          ) : (
            <>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <BreadcrumbLink href="/products" className="text-sm font-normal">
                    Products
                  </BreadcrumbLink>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>

      <div>
        <div className="mb-4 flex flex-row items-center justify-between">
          <div>
            <p className="text-xl font-medium text-black">Products</p>
            <p className="text-xs font-normal text-slate-500">{products.totalCount} products found</p>
          </div>
          <Button variant={'ghost'} className="rounded-full md:hidden" onClick={() => setOpenFilter((prev) => !prev)}>
            <Filter className="text-gray-600" width={22} height={22} />
          </Button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9 pr-9"
            placeholder="Search products…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchInput && (
            <button
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={handleSearchClear}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {!selectedCategory && !searchInput && (
          <ExploreCategories
            title={false}
            onCategoryClick={handleCategory}
            data={categories as unknown as Awaited<ReturnType<typeof getCategoryList>>}
          />
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <div
          // className="col-span-3 lg:col-span-1 lg:flex"
          className={openFilter ? `col-span-3 lg:col-span-1` : `col-span-3 hidden lg:col-span-1 lg:flex`}
        >
          <FilterSideBar type="product" auctionHouses={auctionHouses} categories={categories} />
        </div>
        <div className="col-span-3 flex flex-col gap-10">
          {navigation.state === 'loading' ? (
            <div className="flex w-full items-center justify-center">
              <LoaderCircle className="animate-spin" />
            </div>
          ) : (
            <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {productsData.length > 0 ? (
                productsData.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product as unknown as Awaited<ReturnType<typeof getProductList>>['list'][number]}
                    auctionType={(product.auction?.auctionType || '') as AuctionType}
                  />
                ))
              ) : (
                <span className="col-col-span-1 text-center text-sm text-slate-500 md:col-span-2 lg:col-span-3">
                  No data found
                </span>
              )}
            </div>
          )}

          <CustomPagination
            limit={products.limit}
            totalCount={products.totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrevPage={() => {
              searchParams.set('page', (currentPage - 1).toString());
              setSearchParams(searchParams);
            }}
            onNextPage={() => {
              searchParams.set('page', (currentPage + 1).toString());
              setSearchParams(searchParams);
            }}
            onPageChange={(page) => {
              searchParams.set('page', page);
              setSearchParams(searchParams);
            }}
            onLimitChange={(limit) => {
              searchParams.set('limit', limit);
              setSearchParams(searchParams);
            }}
          />
        </div>
      </div>
    </Layout>
  );
}
